<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-toolbar class="md-success">
        <div class="md-toolbar-row">
          <div class="md-toolbar-section-start">
            <hierarchy-menu :selectableGroup="false"></hierarchy-menu>
          </div>

          <md-button class="md-button md-just-icon md-simple" @click="openModal">
            <i style="color: #ffffff !important;" class="fas fa-cog"></i>
          </md-button>
        </div>
      </md-toolbar>
      <br />
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-33">
      <stats-card header-color="danger">
        <template slot="header">
          <div class="card-icon">
            <i class="fas fa-thermometer-full"></i>
          </div>
          <p class="category">{{$lang.charts.temperature}}</p>
          <h3 class="title">
            <animated-number
              :value="averageTemp"
              :formatValue="tempFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.T.MIN"
                  :formatValue="tempFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.T.AVG"
                  :formatValue="tempFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.T.MAX"
                  :formatValue="tempFormat"
                  :duration="animationDuration"
                />
              </b>
              | {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-33">
      <stats-card header-color="blue">
        <template slot="header">
          <div class="card-icon">
            <i class="fa fa-tint"></i>
          </div>
          <p class="category">{{$lang.charts.humidity}}</p>
          <h3 class="title">
            <animated-number
              :value="averageHum"
              :formatValue="humidityFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.H.MIN"
                  :formatValue="humidityFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.H.AVG"
                  :formatValue="humidityFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.H.MAX"
                  :formatValue="humidityFormat"
                  :duration="animationDuration"
                />
              </b>
              | {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-33">
      <stats-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <i class="fa fa-industry"></i>
          </div>
          <p class="category">{{$lang.charts.co2}}</p>
          <h3 class="title">
            <animated-number
              :value="averageCO2"
              :formatValue="co2Format"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.C.MIN"
                  :formatValue="co2Format"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.C.AVG"
                  :formatValue="co2Format"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.C.MAX"
                  :formatValue="co2Format"
                  :duration="animationDuration"
                />
              </b>
              | {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-33">
      <stats-card header-color="warning">
        <template slot="header">
          <div class="card-icon">
            <i class="fa fa-sun"></i>
          </div>
          <p class="category">{{$lang.charts.lux1}}</p>
          <h3 class="title">
            <animated-number
              :value="averageLux"
              :formatValue="luxFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.l1.MIN"
                  :formatValue="luxFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.l1.AVG"
                  :formatValue="luxFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.l1.MAX"
                  :formatValue="luxFormat"
                  :duration="animationDuration"
                />
              </b>
              | {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>


    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-33">
      <stats-card header-color="warning">
        <template slot="header">
          <div class="card-icon">
            <i class="fa fa-sun"></i>
          </div>
          <p class="category">{{$lang.charts.lux2}}</p>
          <h3 class="title">
            <animated-number
              :value="averageLux2"
              :formatValue="luxFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.l2.MIN"
                  :formatValue="luxFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.l2.AVG"
                  :formatValue="luxFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.l2.MAX"
                  :formatValue="luxFormat"
                  :duration="animationDuration"
                />
              </b>
              | {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>


    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-33">
      <stats-card header-color="primary">
        <template slot="header">
          <div class="card-icon">
            <i class="fas fa-running"></i>
          </div>
          <p class="category">{{$lang.charts.motion_sensor}}</p>
          <h3 class="title">
            <animated-number
              :value="averagePir"
              :formatValue="pirFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.pir.MIN"
                  :formatValue="pirFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.pir.AVG"
                  :formatValue="pirFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.pir.MAX"
                  :formatValue="pirFormat"
                  :duration="animationDuration"
                />
              </b>
              | {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>


    <div class="md-layout-item md-size-100">
      
        <md-card>
          <md-card-header class="md-card-header-icon md-card-header-blue">
            <graph-tool-bar :showSource="true" :showHierarchy="false" granularity="day" @on-selected="onPanelSelect" ></graph-tool-bar>
          </md-card-header>
  
          <md-card-content>
         <highcharts class="charts" :options="chartOptions" ref="areaChart"></highcharts>
          </md-card-content>
        </md-card>
    
    </div>

    <div class="md-layout-item md-size-80">
      <global-sales-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <md-icon>language</md-icon>
          </div>
          <h4 class="title">{{$lang.charts.locations}}</h4>
        </template>

        <template slot="content">
          <devices-stats-panel :settings="devicesStatsData"></devices-stats-panel>
          <!-- <div id="map"></div> -->
        </template>
      </global-sales-card>
    </div>

    <div class="md-layout-item md-size-20">
      <global-sales-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <md-icon>share</md-icon>
          </div>
          <h4 class="title">{{$lang.charts.share}}</h4>
        </template>

        <template slot="content">
         <qr-code :text="qrcode"></qr-code>
        </template>
      </global-sales-card>
    </div>

    <modal v-if="showModal">
      <template slot="header">
        <h4 class="modal-title">{{$lang.titles.dashboard_settings}}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="closeModal">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <h4 class="card-title">{{$lang.titles.statistics_report}}</h4>
        <md-radio v-model="selectedDateRange" value="today">{{$lang.keys.today_stats}}</md-radio>
        <md-radio v-model="selectedDateRange" value="week">{{$lang.keys.week}}</md-radio>
        <md-radio v-model="selectedDateRange" value="month">{{$lang.keys.month}}</md-radio>
        <md-radio v-model="selectedDateRange" value="year">{{$lang.keys.year}}</md-radio>

        <h4 class="card-title">{{$lang.titles.graphs_types}}</h4>
        <p>{{$lang.titles.active_energy_report}}</p>
        <md-checkbox class="md-primary" v-model="graphTypes" value="3">{{$lang.keys.monthly}}</md-checkbox>
        <md-checkbox class="md-primary" v-model="graphTypes" value="2">{{$lang.keys.daily}}</md-checkbox>
        <md-checkbox class="md-primary" v-model="graphTypes" value="1">{{$lang.keys.hourly}}</md-checkbox>

        <p>{{$lang.titles.reactive_energy_report}}</p>
        <md-checkbox class="md-primary" v-model="graphTypes" value="6">{{$lang.keys.monthly}}</md-checkbox>
        <md-checkbox class="md-primary" v-model="graphTypes" value="5">{{$lang.keys.daily}}</md-checkbox>
        <md-checkbox class="md-primary" v-model="graphTypes" value="4">{{$lang.keys.hourly}}</md-checkbox>
      </template>

      <template slot="footer">
        <md-button class="md-primary" @click="saveModalData">{{$lang.keys.save}}</md-button>
        <md-button class="md-simple" @click="closeModal">{{$lang.keys.close}}</md-button>
      </template>
    </modal>


    <snack-bar ref="snackbar"></snack-bar>
  </div>
</template>

<script>
import { SlideYDownTransition } from "vue2-transitions";
import MarkerClusterer from "@google/markerclusterer";
import gmapsInit from "@/utils/gmaps";
import { Modal } from "@/components";
import snackBar from "@/components/SnackBar";
import hierarchyMenu from "@/components/HierarchyMenu";
import AnimatedNumber from "animated-number-vue";

import graphToolBar from "@/components/GraphToolBar";
//import chartOptions from "@/config/charts/column";
import areaChartOptions from "@/config/charts/area";
import axios from "axios";
import { getSelectedDeviceToken, getUserToken } from "@/storage/memoryStorage";

import format from "date-fns/format";
import subHours from "date-fns/sub_hours";
import subDays from "date-fns/sub_days";
import start_of_today from "date-fns/start_of_today";
import end_of_today from "date-fns/end_of_today";
import startOfWeek from "date-fns/start_of_week";
import endOfWeek from "date-fns/end_of_week";
import startOfMonth from "date-fns/start_of_month";
import endOfMonth from "date-fns/end_of_month";
import startOfYear from "date-fns/start_of_year";
import endOfYear from "date-fns/end_of_year";
import endOfTomorrow from "date-fns/end_of_tomorrow";
import startOfHour from "date-fns/start_of_hour";

import { StatsCard, GlobalSalesCard } from "@/components";
import { startOfDay, subMonths } from "date-fns";
import { types } from "util";

import DevicesStatsPanel from "@/components/Dashboard/DevicesStatsPanel";
import CryptoJS from 'crypto-js';

export default {
  components: {
    "hierarchy-menu": hierarchyMenu,
    "graph-tool-bar": graphToolBar,
    "snack-bar": snackBar,
    Modal,
    AnimatedNumber,
    StatsCard,
    GlobalSalesCard,
    SlideYDownTransition,
    DevicesStatsPanel
  },

  data() {
    return {
      chartOptions: null,
      animationDuration: 500,
      showModal: false,

      selectedDateRange: "",
      graphTypes: [],
      graphDraw: [],

      averageTemp: "",
      averageHum: "",
      averageCO2: "",
      averageLux: "",
      averageLux2: "",
      averagePir: "",

      statisticData: {
        loading: false,
        T: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        H: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        C: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        l1: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        l2: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        pir: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        }
        
      },

      locations: [],
      devicesStatsData: [],
      qrcode: ""
    };
  },

  computed: {
    WSmessage() {
      return this.$store.getters.getDataMessage;
    },
    selectedDate() {
      return this.$store.getters.getDateRange;
    },
    selectedDevice() {
      return this.$store.getters.getSelectedDevice;
    },
    deviceList() {
      return this.$store.getters.getDeviceList;
    },
    statsDate() {
      return this.$store.getters.getSettings.statsDate;
    },
    selectedGraphs() {
      return this.$store.getters.getSettings.graphs;
    },
    overviewStatsUpdate() {
      return this.$store.getters.getOverviewStats;
    },
    selectedyAxisType() {
      return this.$store.getters.getSettings.yAxisType;
    }
  },

/*     
tempS: 0.0,
hum: 0.0,
      co2: 0,
      lux: 0.0,
      lux2: 0,
      pir: 0*/

  watch: {
    WSmessage: function(payload, oldPayload) {
      this.averageTemp = 0;
      this.averageHum = 0;
      this.averageCO2 = 0;
      this.averageLux = 0;
      this.averageLux2 = 0;
      this.averagePir = 0;

      if (payload.tempS) {
        this.averageTemp = payload.tempS;
      }

      if (payload.hum) {
        this.averageHum = payload.hum;
      }

      if (payload.co2) {
        this.averageCO2 = payload.co2;
      }

      if (payload.lux) {
        this.averageLux = payload.lux;
      }

      if (payload.lux2) {
        this.averageLux2 = payload.lux2;
      }

      if (payload.pir) {
        this.averagePir = payload.pir;
      }

    },
    selectedDevice: function(newDevice, oldDevice) {
        //if (newDevice.mac !== oldDevice.mac || newDevice.macList) {
          this.getQRString();
          this.getStatisticsData();
        
          this.getDataToCharts();
          
          
        //}
    },
    overviewStatsUpdate: function(newVal, oldVal) {
      if (newVal) {
        this.getHierarchyData();
        this.$store.commit("setOverviewStats", false);
      }
    }

    /*     statsDate: function(newDevice, oldDevice) {
      if (newDevice.from !== oldDevice.from || newDevice.to !== oldDevice.to) {
        this.getStatisticsData();
        this.getEnergyData();
      }
    } */
  },

  created() {
    this.getQRString();
    this.getHierarchyData();

    this.selectedDateRange = this.statsDate.selector;
    this.graphTypes = this.selectedGraphs;

     

    this.chartOptions = JSON.parse(JSON.stringify(areaChartOptions));
    this.chartOptions.title.text = this.$lang.charts.temperature+" / "+this.$lang.charts.humidity+" / "+this.$lang.charts.co2+" / "+this.$lang.charts.lux1+" / "+this.$lang.charts.lux2+" / "+this.$lang.charts.motion_sensor;
    this.chartOptions.yAxis = [];
    this.chartOptions.tooltip.pointFormat =
      "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y:.1f} °C</b><br/>";

    this.chartOptions.chart.events = {
      redraw: function(event) {
        this.hideLoading();
      }
    };



   
  },

  mounted() {
    
    let compStatsDateRange = this.getTSFromDateRange(this.selectedDateRange);

    if (
      compStatsDateRange.from !== this.statsDate.from ||
      compStatsDateRange.to !== this.statsDate.to
    ) {
      compStatsDateRange.selector = this.selectedDateRange;
      this.$store.commit("setstatsDate", compStatsDateRange);
    }

   
    this.getStatisticsData();
    this.setChartsData(this.graphTypes);
    

  },

  methods: {
    localizedDateRange() {
      switch (this.selectedDateRange) {
        case "today":
          return this.$lang.keys.today_stats;
        case "week":
          return this.$lang.keys.week;
        case "month":
          return this.$lang.keys.month;
        case "year":
          return this.$lang.keys.year;
      }
    },

    getTSFromDateRange(dateRange) {
      let statDate = {
        selector: dateRange,
        from: 0,
        to: 0
      };
      switch (dateRange) {
        case "today":
          statDate.from = format(start_of_today(), "X");
          statDate.to = format(end_of_today(), "X");
          break;
        case "week":
          statDate.from = format(
            startOfWeek(start_of_today(), {
              weekStartsOn: 1
            }),
            "X"
          );
          statDate.to = format(
            endOfWeek(start_of_today(), {
              weekStartsOn: 1
            }),
            "X"
          );
          break;
        case "month":
          statDate.from = format(startOfMonth(start_of_today()), "X");
          statDate.to = format(endOfMonth(start_of_today()), "X");
          break;
        case "year":
          statDate.from = format(startOfYear(start_of_today()), "X");
          statDate.to = format(endOfYear(start_of_today()), "X");
          break;
      }
      return statDate;
    },

    saveModalData: function() {
      let compStatsDateRange = this.getTSFromDateRange(this.selectedDateRange);

      if (
        this.statsDate.from !== compStatsDateRange.from ||
        this.statsDate.to !== compStatsDateRange.to
      ) {
        compStatsDateRange.selector = this.selectedDateRange;
        this.$store.commit("setstatsDate", compStatsDateRange);
        this.getStatisticsData();
       
      }

      this.$store.commit("setSelectedGraphs", this.graphTypes);
      this.setChartsData(this.graphTypes);

      this.closeModal();
    },

    setChartsData(graphsArray) {
      this.graphDraw = [];
      graphsArray.forEach(gSelect => {
        const obj = {
          id: gSelect,
          type: Number(gSelect) <= 3 ? 1 : 2,
          ref: "graph_" + gSelect,
          title: "graph_" + gSelect
        };

        switch (gSelect) {
          case "1":
            obj.title = this.$lang.charts.active_energy_last_24_hours;
            break;
          case "2":
            obj.title = this.$lang.charts.active_energy_last_14_days;
            break;
          case "3":
            obj.title = this.$lang.charts.active_energy_last_12_months;
            break;
          case "4":
            obj.title = this.$lang.charts.reactive_energy_last_24_hours;
            break;
          case "5":
            obj.title = this.$lang.charts.reactive_energy_last_14_days;
            break;
          case "6":
            obj.title = this.$lang.charts.reactive_energy_last_12_months;
            break;
        }

        this.graphDraw.push(obj);
      });
      this.getDataToCharts();
    },

    getDataToCharts() {
      
      this.$nextTick(() => {
        this.graphDraw.forEach(el => {
          let granularity = "";
          let from = 0;
          let to = 0;
          let dt;

          switch (el.id) {
            case "1":
            case "4":
              granularity = "HOUR";
              dt = new Date();
              dt.setHours(dt.getHours() + 1);
              to = format(startOfHour(dt), "X");
              from = format(subHours(startOfHour(dt), 24), "X");
              break;
            case "2":
            case "5":
              granularity = "DAY";
              dt = new Date();
              dt.setDate(dt.getDate() + 1);
              to = format(startOfDay(dt), "X");
              from = format(subDays(startOfDay(dt), 14), "X");
              break;
            case "3":
            case "6":
              granularity = "MONTH";
              dt = new Date();
              dt.setMonth(dt.getMonth() + 1);
              to = format(startOfMonth(dt), "X");
              from = format(subMonths(startOfMonth(dt), 12), "X");
              break;
          }

          this.getEnergyChartData(
            from,
            to,
            this.$refs[el.ref][0],
            granularity,
            el.type
          );
        });
      });
    },



    openModal() {
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    getStatisticsData() {
      this.statisticData.loading = true;

      let axiosConfig = {
        method: "POST",
        data: {
          milliseconds: true,
          statistics: true,
          from: this.statsDate.from,
          to: this.statsDate.to
        },
        withCredentials: true
      };


        axiosConfig.url = this.$ENDPOINTS.API_R + "/sensor/data";
        axiosConfig.data.token = getSelectedDeviceToken(
          this.selectedDevice.mac
        );
      

      
      axios(axiosConfig)
        .then(response => {
          if (response.data.status === "OK") {
            this.statisticData.T.MIN = response.data.data.statistics.temp_1.MIN;
            this.statisticData.T.AVG = response.data.data.statistics.temp_1.AVG;
            this.statisticData.T.MAX = response.data.data.statistics.temp_1.MAX;

            this.statisticData.H.MIN = response.data.data.statistics.humidity.MIN;
            this.statisticData.H.AVG = response.data.data.statistics.humidity.AVG;
            this.statisticData.H.MAX = response.data.data.statistics.humidity.MAX;

            this.statisticData.C.MIN = response.data.data.statistics.co2.MIN;
            this.statisticData.C.AVG = response.data.data.statistics.co2.AVG;
            this.statisticData.C.MAX = response.data.data.statistics.co2.MAX;

            this.statisticData.l1.MIN = response.data.data.statistics.lux1.MIN;
            this.statisticData.l1.AVG = response.data.data.statistics.lux1.AVG;
            this.statisticData.l1.MAX = response.data.data.statistics.lux1.MAX;

            this.statisticData.l2.MIN = response.data.data.statistics.lux2.MIN;
            this.statisticData.l2.AVG = response.data.data.statistics.lux2.AVG;
            this.statisticData.l2.MAX = response.data.data.statistics.lux2.MAX;

            this.statisticData.pir.MIN = response.data.data.statistics.pir.MIN;
            this.statisticData.pir.AVG = response.data.data.statistics.pir.AVG;
            this.statisticData.pir.MAX = response.data.data.statistics.pir.MAX;

            this.statisticData.loading = false;
          }
        })
        .catch(error => {
          if (error.response.data.status == "FAIL") {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    },

    getHierarchyData() {
      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/facility/devices-hierarchy",
        data: {
          token: getUserToken()
        },
        withCredentials: true
      })
        .then(response => {
          response.data.data.tags.forEach(element => {
            if (
              element.hasOwnProperty("lat") &&
              element.hasOwnProperty("lng")
            ) {
              const deviceListIndex = this.deviceList.findIndex(
                e => e.mac == element.mac
              );

              const mapMarker = {
                position: {
                  lat: Number(element.lat),
                  lng: Number(element.lng)
                },
                data: {
                  title: element.title,
                  tag: element.tags[0],
                  mac: element.mac,
                  status: this.deviceList[deviceListIndex].status
                }
              };

              this.locations.push(mapMarker);
            }
            if (element.tags.length > 0) {
              this.devicesStatsData.push(element.tags[0]);
            }
          });
          //this.initMap();
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.code != 610) {
              this.$refs.snackbar.notify(
                this.$lang.messages.api_communication_error
              );
            }
          }
          //this.initMap();
        });
    },






    tempFormat(value) {
      return `${Number(value).toFixed(1)} °C`;
    },

    humidityFormat(value) {
        return `${Number(value).toFixed(0)} %`;
    },

    co2Format(value) {
        return `${Number(value).toFixed(0)} ppm`;
    },

    luxFormat(value) {
        return `${Number(value).toFixed(0)} lux`;
    },

    pirFormat(value) {
        return `${Number(value).toFixed(0)} `;
    },


    getQRString(){

      let axiosConfig = {
        method: "POST",
        data: {
          deviceMAC: this.selectedDevice.mac,
          token: getUserToken(),
          name: this.selectedDevice.title,
          encode: true
        },
        withCredentials: true,
        url: this.$ENDPOINTS.API_R + "/qrtoken"
      };
      
      axios(axiosConfig).then(response => {
        
          if (response.data.status === "OK") 
          {
            this.qrcode = response.data.data.token;
          }
        })
        .catch(error => {
         
          if (error.response.data.status == "FAIL")
          {
            this.$refs.snackbar.notify(this.$lang.messages.api_communication_error+" QR");
          }
        });

    },

    // async initMap() {
    //   try {
    //     const google = await gmapsInit();
    //     const geocoder = new google.maps.Geocoder();
    //     const map = new google.maps.Map(document.getElementById("map"), {
    //       gestureHandling: "cooperative"
    //     });

    //     function placeMarkerAndPanTo(latLng, map) {
    //       var marker = new google.maps.Marker({
    //         position: latLng,
    //         map: map
    //       });
    //       map.panTo(latLng);
    //     }

    //     geocoder.geocode({ address: `Slovakia` }, (results, status) => {
    //       if (status !== `OK` || !results[0]) {
    //         throw new Error(status);
    //       }

    //       map.setCenter(results[0].geometry.location);
    //       map.fitBounds(results[0].geometry.viewport);
    //     });

    //     /*        adding tag by click
    //      map.addListener("click", function(e) {
    //       placeMarkerAndPanTo(e.latLng, map);
    //     }); */

    //     const markerClickHandler = marker => {
    //       map.setZoom(18);
    //       map.setCenter(marker.getPosition());

    //       var contentString =
    //         '<div id="content">' +
    //         '<div id="siteNotice">' +
    //         "</div>" +
    //         '<h4 id="firstHeading" class="firstHeading">' +
    //         marker.data.title +
    //         "</h4>" +
    //         '<div id="bodyContent">' +
    //         this.$lang.general.device +
    //         ": " +
    //         marker.data.tag +
    //         "</br>" +
    //         "MAC: " +
    //         marker.data.mac +
    //         "</br>" +
    //         this.$lang.general.status +
    //         ": " +
    //         marker.data.status +
    //         "</br>" +
    //         "</div>" +
    //         "</div>";

    //       var infowindow = new google.maps.InfoWindow({
    //         content: contentString
    //       });

    //       infowindow.open(map, marker);
    //     };

    //     const markers = this.locations.map(location => {
    //       const marker = new google.maps.Marker({ ...location, map });
    //       marker.addListener(`click`, () => markerClickHandler(marker));

    //       return marker;
    //     });

    //     /*       new MarkerClusterer(map, markers, {
    //     imagePath: `https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m`,
    //   }); */
    //   } catch (error) {
    //     console.error(error);
    //   }
    // }


    onPanelSelect(data) {
      this.$nextTick(() => {
        this.getAPIData(data);
      });
    },

    getAPIData(selected) {
      const { chart } = this.$refs.areaChart;
      chart.showLoading();

      while (chart.series.length > 0) {
        chart.series[0].remove(false, false);
      }

      while (chart.yAxis.length > 0) {
        chart.yAxis[0].remove(false, false);
      }

      this.chartOptions.series = [];
      this.chartOptions.yAxis = [];


////////// TEMP
        this.chartOptions.yAxis.push(
          { // temperature axis
            title: {
        text: null
      },
            labels: {
              format: '{value:.1f} °C'
              },
          }
        );
//// hum
        this.chartOptions.yAxis.push(
          { // humidity axis
            title: {
        text: null
      },
      max: 100,
      min:0,
            labels: {
              format: '{value:.0f} %'
              }
          }
        );

        ////co2
        this.chartOptions.yAxis.push(
          { // co2 axis
            title: {
        text: null
      },
      visible: false,
            labels: {
              format: '{value:.0f} ppm'
              }
          }
        );


                ////lux1
                this.chartOptions.yAxis.push(
          { // co2 axis
            title: {
        text: null
      },
      visible: false,
            labels: {
              format: '{value:.0f} lux'
              }
          }
        );

                ////lux
                this.chartOptions.yAxis.push(
          { // co2 axis
            title: {
        text: null
      },
      visible: false,
            labels: {
              format: '{value:.0f} alux'
              }
          }
        );

                ////pir
                this.chartOptions.yAxis.push(
          { // co2 axis
            title: {
        text: null
      },
      visible: false,
            labels: {
              format: '{value:.0f}'
              }
          }
        );

//////// temp
        this.chartOptions.series.push({
          name: this.$lang.charts.temperature,
         yAxis: 0,
          type: "line",
          visible: true,
          marker: {
            enabled: false
          },
          color: '#FF3333',
          events: {
            legendItemClick: function(event) {
             // onPhaseSelect(this.phases[i], event.target.visible);
            }.bind(this)
          },
          data: []
        });

///////////// HUM


        this.chartOptions.series.push({
          name: this.$lang.charts.humidity,
         yAxis: 1,
          type: "line",
          visible: true,
          marker: {
            enabled: false
          },
          tooltip: {
            pointFormat :  "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y:.0f} %</b><br/>"
          },
          color: '#0000FF',
          events: {
            legendItemClick: function(event) {
             // onPhaseSelect(this.phases[i], event.target.visible);
            }.bind(this)
          },
          data: []
        });


        //////////////// CO2

        

        this.chartOptions.series.push({
          name: this.$lang.charts.co2,
          yAxis: 2,
          type: "line",
          visible: true,
          marker: {
            enabled: false
          },
          tooltip: {
            pointFormat :  "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y:.0f} ppm</b><br/>"
          },
          color: '#00FF00',
          events: {
            legendItemClick: function(event) {
             // onPhaseSelect(this.phases[i], event.target.visible);
            }.bind(this)
          },
          data: []
        });

  

                //////////////// lux1

        

                this.chartOptions.series.push({
          name: this.$lang.charts.lux1,
          yAxis: 3,
          type: "line",
          visible: true,
          marker: {
            enabled: false
          },
          tooltip: {
            pointFormat :  "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y:.0f} lux</b><br/>"
          },
          color: '#FFFF00',
          events: {
            legendItemClick: function(event) {
             // onPhaseSelect(this.phases[i], event.target.visible);
            }.bind(this)
          },
          data: []
        });

                //////////////// lux2

        

                this.chartOptions.series.push({
          name: this.$lang.charts.lux2,
          yAxis: 4,
          type: "line",
          visible: true,
          marker: {
            enabled: false
          },
          tooltip: {
            pointFormat :  "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y:.0f} alux</b><br/>"
          },
          color: '#B0FF00',
          events: {
            legendItemClick: function(event) {
             // onPhaseSelect(this.phases[i], event.target.visible);
            }.bind(this)
          },
          data: []
        });
      
                      //////////////// PIR

        

         this.chartOptions.series.push({
          name: this.$lang.charts.motion_sensor,
          yAxis: 5,
          type: "area",
          visible: true,
          marker: {
            enabled: false
          },
          tooltip: {
            pointFormat :  "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y:.0f}</b><br/>"
          },
          color: '#909090',
          events: {
            legendItemClick: function(event) {
             // onPhaseSelect(this.phases[i], event.target.visible);
            }.bind(this)
          },
          data: []
        });

      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/sensor/data",
        data: {
          milliseconds: false,
          data: true,
          from: selected.date.from,
          to: selected.date.to,
          token:  getSelectedDeviceToken(selected.device.mac)
        },
        withCredentials: true
      })
        .then(response => {
           var temperatures = [];
           var humidities = [];
           var co2s = [];
           var lux1s = [];
           var lux2s = [];
           var pirs = [];
          
           for (let i=0; i<response.data.data.timestamps.length && i<response.data.data.temp_1.length; i++)
           {
           temperatures.push([response.data.data.timestamps[i]*1000,response.data.data.temp_1[i]]);
           }

           for (let i=0; i<response.data.data.timestamps.length && i<response.data.data.humidity.length; i++)
           {
           humidities.push([response.data.data.timestamps[i]*1000,response.data.data.humidity[i]]);
           }

           for (let i=0; i<response.data.data.timestamps.length && i<response.data.data.co2.length; i++)
           {
            co2s.push([response.data.data.timestamps[i]*1000,response.data.data.co2[i]]);
           }

           for (let i=0; i<response.data.data.timestamps.length && i<response.data.data.lux1.length; i++)
           {
            lux1s.push([response.data.data.timestamps[i]*1000,response.data.data.lux1[i]]);
           }

           for (let i=0; i<response.data.data.timestamps.length && i<response.data.data.lux2.length; i++)
           {
            lux2s.push([response.data.data.timestamps[i]*1000,response.data.data.lux2[i]]);
           }

           for (let i=0; i<response.data.data.timestamps.length && i<response.data.data.pir.length; i++)
           {
            pirs.push([response.data.data.timestamps[i]*1000,response.data.data.pir[i]]);
           }

            chart.series[0].setData(
              temperatures,
              false,
              false,
              false
            );

            chart.series[1].setData(
              humidities,
              false,
              false,
              false
            );
            
            chart.series[2].setData(
              co2s,
              false,
              false,
              false
            );

            chart.series[3].setData(
              lux1s,
              false,
              false,
              false
            );

            chart.series[4].setData(
              lux2s,
              false,
              false,
              false
            );

            chart.series[5].setData(
              pirs,
              false,
              false,
              false
            );
          

          chart.xAxis[0].setExtremes(null, null, true);
        })
        .catch(error => {
          if (error.response.data.code == 610) {
            this.$refs.snackbar.notify(this.$lang.messages.data_not_found);
           
              chart.series[0].setData([]);
              chart.series[1].setData([]);
              chart.series[2].setData([]);
              chart.series[3].setData([]);
              chart.series[4].setData([]);
              chart.series[5].setData([]);
    
           
            
          } else {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    }
  }
};
</script>

<style scoped>
#map {
  margin: 0;
  max-height: 500px;
}

.normal-header {
  padding: 0 !important;
  min-height: 0px;
}

/* .md-button.md-simple i,
.md-button.md-simple:hover i {
  color: #ffffff !important;
} */

.md-toolbar {
  position: inherit;
}

.modal-header {
  opacity: 1;
}

.card-title {
  margin: 10px;
}

.modal-body {
  padding: 0px 0px 16px;
}

.modal-body p {
  margin: 5px;
}

.dashboardChart {
  height: 350px;
}
</style>
